import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { ActivityIndicator } from 'react-native';

import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { useCampaignStore } from '@/stores/campaignStore';
import { useNavigationStore } from '@/stores/navigationStore';

export default function PlansRedirectScreen() {
  const { battlePlanId, engagementId } = useLocalSearchParams<{ 
    battlePlanId: string; 
    engagementId: string; 
  }>();
  const router = useRouter();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { getSelectedBranch, getCampaigns } = useNavigationStore();

  useEffect(() => {
    const findCampaignAndRedirect = async () => {
      if (!battlePlanId || !engagementId) {
        setError('Missing battle plan or engagement ID');
        setLoading(false);
        return;
      }

      try {
        const selectedBranch = getSelectedBranch();
        if (!selectedBranch) {
          setError('No branch selected');
          setLoading(false);
          return;
        }

        const branchId = selectedBranch.branch_short_name;
        const campaigns = getCampaigns(branchId);
        
        if (!campaigns || campaigns.length === 0) {
          setError('No campaigns found for this branch');
          setLoading(false);
          return;
        }

        // Search through all campaigns to find which one contains this battle plan
        let foundCampaignId: string | null = null;
        let battlePlanType: string | null = null;
        
        const campaignStore = useCampaignStore.getState();
        const navigationStore = useNavigationStore.getState();
        
        for (const campaign of campaigns) {
          try {
            // Set the campaign context temporarily
            navigationStore.setSelectedCampaignId(branchId, campaign.boundary_id);
            campaignStore.syncWithNavigationStore();
            
            // Fetch battle plans for this campaign
            const battlePlans = await campaignStore.fetchBattlePlans();
            
            // Check if this campaign has the battle plan and get its type
            const battlePlan = battlePlans.find(
              bp => bp.battle_plan_id === battlePlanId
            );
            
            if (battlePlan) {
              foundCampaignId = campaign.boundary_id;
              battlePlanType = battlePlan.battle_plan_type;
              break;
            }
          } catch (error) {
            // Continue to next campaign if this one fails
            console.warn(`Failed to fetch battle plans for campaign ${campaign.boundary_id}:`, error);
            continue;
          }
        }

        if (foundCampaignId) {
          // Check if this is a gotv-telling battle plan
          if (battlePlanType === 'gotv-telling') {
            // For gotv-telling, we need to find the polling station for this engagement
            // and redirect to the telling page
            try {
              const campaignStore = useCampaignStore.getState();
              const navigationStore = useNavigationStore.getState();
              
              // Set the campaign context
              navigationStore.setSelectedCampaignId(branchId, foundCampaignId);
              campaignStore.syncWithNavigationStore();

              //always fetch new polling stations as may have updated access
              const pollingStations = await campaignStore.fetchPollingStations(battlePlanId);

              console.log('pollingStations', pollingStations);
              
              if (pollingStations && pollingStations.length > 0) {
                // Find the polling station that contains this engagement
                const pollingStation = pollingStations.find(ps => 
                  ps.polling_station_districts?.some(district => 
                    district.engagement_id === engagementId
                  )
                );
                
                if (pollingStation) {
                  // Redirect to the telling page with polling station ID
                  router.replace({
                    pathname: '/(main)/[branch]/campaign/[campaignId]/telling/[battlePlanId]/[pollingStationId]',
                    params: {
                      branch: branchId,
                      campaignId: foundCampaignId,
                      battlePlanId: battlePlanId,
                      pollingStationId: pollingStation.polling_station_id.toString(),
                    },
                  });
                  return;
                }
              }
              
              // If we couldn't find the polling station, show error
              setError('Could not find polling station for this telling engagement');
              setLoading(false);
            } catch (error) {
              console.error('Error finding polling station for telling engagement:', error);
              setError('Failed to find polling station for this engagement');
              setLoading(false);
            }
          } else {
            // For other battle plan types, redirect to canvass
            router.replace({
              pathname: '/(main)/[branch]/campaign/[campaignId]/canvass/[battlePlanId]/[engagementId]',
              params: {
                branch: branchId,
                campaignId: foundCampaignId,
                battlePlanId: battlePlanId,
                engagementId: engagementId,
              },
            });
          }
        } else {
          setError(`Battle plan ${battlePlanId} not found in any campaign`);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error finding campaign for battle plan:', error);
        setError('Failed to find campaign for this battle plan');
        setLoading(false);
      }
    };

    findCampaignAndRedirect();
  }, [battlePlanId, engagementId, router, getSelectedBranch, getCampaigns]);

  if (loading) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" />
        <ThemedText style={{ marginTop: 16 }}>Finding campaign...</ThemedText>
      </ThemedView>
    );
  }

  if (error) {
    return (
      <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <ThemedText style={{ textAlign: 'center', color: 'red' }}>
          {error}
        </ThemedText>
        <ThemedText 
          style={{ textAlign: 'center', marginTop: 16, opacity: 0.7 }}
          onPress={() => router.back()}
        >
          Tap to go back
        </ThemedText>
      </ThemedView>
    );
  }

  return (
    <ThemedView style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
      <ThemedText>Redirecting...</ThemedText>
    </ThemedView>
  );
}
